import { CirclePersonResponse } from "../../common/models/schema/CirclePersonResopnse";
import { Box, Typography, Grid, Card, CardContent } from "@mui/material";

type CircleProfileDetailsProps = {
  person: CirclePersonResponse;
};

const CircleProfileDetails = (props: CircleProfileDetailsProps) => {
  const { person } = props;

  // Helper function to render field if not null/empty
  const renderField = (label: string, value: any) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
          {typeof value === "boolean" ? (value ? "Yes" : "No") : String(value)}
        </Typography>
      </Grid>
    );
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Helper function to render profile field
  const renderProfileField = (profileField: any) => {
    if (!profileField || !profileField.label) return null;

    let value = "";
    if (profileField.community_member_profile_field) {
      const field = profileField.community_member_profile_field;
      value = field.text || field.textarea || field.display_value || "";
    }

    if (!value) return null;

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {profileField.label}
        </Typography>
        <Typography variant="body2">{value}</Typography>
      </Grid>
    );
  };

  // Helper function to render tag
  const renderTag = (tag: any) => {
    if (!tag || !tag.name) return null;

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {tag.name}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
          {tag.display_format || "label"}
          {tag.color && ` (${tag.color})`}
          {tag.emoji && ` ${tag.emoji}`}
        </Typography>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Circle Profile Details
      </Typography>

      {/* Basic Info Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Grid container spacing={2}>
            {renderField("ID", person.id)}
            {renderField("User ID", person.user_id)}
            {renderField("Community ID", person.community_id)}
            {renderField("Public UID", person.public_uid)}
            {renderField("Name", person.name)}
            {renderField("First Name", person.first_name)}
            {renderField("Last Name", person.last_name)}
            {renderField("Email", person.email)}
            {renderField("Headline", person.headline)}
            {renderField("Profile URL", person.profile_url)}
            {renderField("Avatar URL", person.avatar_url)}
          </Grid>
        </CardContent>
      </Card>

      {/* Status Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Status
          </Typography>
          <Grid container spacing={2}>
            {renderField("Active", person.active)}
            {renderField("Accepted Invitation", person.accepted_invitation)}
            {renderField("SSO Provider User ID", person.sso_provider_user_id)}
          </Grid>
        </CardContent>
      </Card>

      {/* Activity Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Activity
          </Typography>
          <Grid container spacing={2}>
            {renderField("Posts Count", person.posts_count)}
            {renderField("Comments Count", person.comments_count)}
            {renderField(
              "Last Seen At",
              person.last_seen_at ? formatDate(person.last_seen_at) : null,
            )}
            {person.activity_score &&
              Object.keys(person.activity_score).length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Activity Score
                  </Typography>
                  <Typography variant="body2">
                    {JSON.stringify(person.activity_score, null, 2)}
                  </Typography>
                </Grid>
              )}
          </Grid>
        </CardContent>
      </Card>

      {/* Gamification Stats */}
      {person.gamification_stats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Gamification Stats
            </Typography>
            <Grid container spacing={2}>
              {renderField(
                "Total Points",
                person.gamification_stats.total_points,
              )}
              {renderField(
                "Current Level",
                person.gamification_stats.current_level,
              )}
              {renderField(
                "Current Level Name",
                person.gamification_stats.current_level_name,
              )}
              {renderField(
                "Points to Next Level",
                person.gamification_stats.points_to_next_level,
              )}
              {renderField(
                "Level Progress",
                person.gamification_stats.level_progress,
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Flattened Profile Fields */}
      {person.flattened_profile_fields && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Profile Information
            </Typography>
            <Grid container spacing={2}>
              {renderField("Bio", person.flattened_profile_fields.bio)}
              {renderField(
                "Location",
                person.flattened_profile_fields.location,
              )}
              {renderField(
                "Profession",
                person.flattened_profile_fields.profession,
              )}
              {renderField(
                "Birthday",
                person.flattened_profile_fields.birthday,
              )}
              {renderField("Company", person.flattened_profile_fields.company)}
              {renderField(
                "Headline",
                person.flattened_profile_fields.headline,
              )}
              {renderField(
                "University",
                person.flattened_profile_fields.university,
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Profile Fields */}
      {person.profile_fields && person.profile_fields.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Custom Profile Fields
            </Typography>
            <Grid container spacing={2}>
              {person.profile_fields.map((field, index) => (
                <div key={index}>{renderProfileField(field)}</div>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Timestamps */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Information
          </Typography>
          <Grid container spacing={2}>
            {renderField(
              "Created At",
              person.created_at ? formatDate(person.created_at) : null,
            )}
            {renderField(
              "Updated At",
              person.updated_at ? formatDate(person.updated_at) : null,
            )}
            {renderField(
              "Profile Confirmed At",
              person.profile_confirmed_at
                ? formatDate(person.profile_confirmed_at)
                : null,
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CircleProfileDetails;
